// src/features/pages/admin/DepartmentsPage.tsx
// Phase 3: company-admin Departments management. Live CRUD against
// employee-service (tenant-scoped server-side); writes gated by department:write.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { nudgeDana } from '@/features/shared/dana/danaNudge';
import toast from 'react-hot-toast';
import { FolderTree, Plus, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { TruncatedText } from '@/features/shared/components/ui/TruncatedText';
import {
  getDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import { RequirePermission } from '@/rbac/RequirePermission';

interface Dept {
  id: string;
  name: string;
  description?: string | null;
  manager_id?: string | null;
  is_active?: boolean;
  employee_count?: number | null;
}

interface DeptSummary {
  department_count: number;
  active_employee_count: number;
  unassigned_employee_count: number;
}

// "1 employee" / "N employees" / "0 employees" — always employee(s), never users.
export const employeeLabel = (n: number) => `${n} ${n === 1 ? 'employee' : 'employees'}`;

// The tenant app mounts under a dynamic prefix (/t/<slug>/app, or bare /app)
// with NO router basename, so an absolute "/employees" escapes the tenant tree
// and hits the dashboard fallback. Derive the base up to and including "/app".
export const tenantAppBase = (pathname: string): string => {
  const m = pathname.match(/^(.*\/app)(?:\/|$)/);
  return m ? m[1] : '/app';
};

const DepartmentsInner: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const confirm = useConfirm();
  const canWrite = useHasPermission('department:write');
  const [departments, setDepartments] = useState<Dept[]>([]);
  const [summary, setSummary] = useState<DeptSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Click a headcount → employee directory filtered to that department + active.
  // The tenant app is mounted under a dynamic prefix (/t/<slug>/app, or /app),
  // with NO router basename — so derive the base from the current path instead
  // of an absolute "/employees" (which escapes the tenant tree → dashboard).
  const viewEmployees = (deptName: string) => {
    const base = tenantAppBase(window.location.pathname);
    navigate(`${base}/employees?department=${encodeURIComponent(deptName)}&status=active`);
  };

  // Inline create/edit form
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Dept | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await getDepartments();
      const items = Array.isArray(res?.items) ? res.items : [];
      setDepartments(items);
      setSummary(res?.summary ?? null);
      if (items.length === 0) nudgeDana({ key: 'departments', message: 'No departments yet — want a hand adding your first one?', question: 'How do I add a department?' });
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('departments.load_error')));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setFormOpen(true);
  };
  const openEdit = (d: Dept) => {
    setEditing(d);
    setName(d.name);
    setDescription(d.description ?? '');
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t('common.name_required'));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateDepartment(editing.id, { name: trimmed, description: description.trim() || null });
        toast.success(t('departments.updated'));
      } else {
        await createDepartment({ name: trimmed, description: description.trim() || undefined });
        toast.success(t('departments.created'));
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, editing ? t('departments.update_error') : t('departments.create_error')), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d: Dept) => {
    if (!(await confirm({ title: 'Delete department', message: t('departments.delete_confirm', { name: d.name }), confirmLabel: 'Delete', danger: true }))) return;
    try {
      await deleteDepartment(d.id);
      toast.success(t('departments.deleted'));
      await load(); // refetch — source of truth (don't rely on an optimistic filter)
    } catch (e: any) {
      const detail: string = e?.response?.data?.detail || '';
      // The backend refuses (400) when the department still has employees. Offer to
      // delete anyway (which unassigns them) instead of failing silently.
      if (e?.response?.status === 400 && /employee/i.test(detail)) {
        const force = await confirm({
          title: 'Department has employees',
          message: `${detail} Delete it anyway and unassign its employees?`,
          confirmLabel: 'Delete anyway',
          danger: true,
        });
        if (!force) return;
        try {
          await deleteDepartment(d.id, true);
          toast.success(t('departments.deleted'));
          await load();
        } catch (e2) {
          toast.error(getApiErrorMessage(e2, t('departments.delete_error')), { duration: 6000 });
        }
        return;
      }
      toast.error(getApiErrorMessage(e, t('departments.delete_error')));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <h1 className="text-h2 text-charcoal">{t('departments.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('departments.subtitle')}</p>
          {summary && !loading && (
            <p className="text-sm text-muted-foreground mt-2">
              <span className="font-medium text-foreground">{summary.department_count}</span>{' '}
              {summary.department_count === 1 ? 'Department' : 'Departments'}
              <span className="mx-1.5">•</span>
              <span className="font-medium text-foreground">{summary.active_employee_count}</span>{' '}
              {summary.active_employee_count === 1 ? 'Active Employee' : 'Active Employees'}
              {summary.unassigned_employee_count > 0 && (
                <>
                  <span className="mx-1.5">•</span>
                  <button
                    type="button"
                    onClick={() => viewEmployees('Unassigned')}
                    className="font-medium text-foreground hover:text-orange-600 hover:underline"
                  >
                    {summary.unassigned_employee_count} Unassigned
                  </button>
                </>
              )}
            </p>
          )}
        </div>
        {canWrite && !formOpen && (
          <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" /> {t('departments.new')}
          </Button>
        )}
      </div>

      {formOpen && (
        <Card className="border-orange-200 dark:border-orange-500/30">
          <CardHeader className="rounded-t-xl flex-row items-center">
            <CardTitle className="flex-1 text-orange-700">
              {editing ? t('departments.edit_title') : t('departments.new_title')}
            </CardTitle>
            <button type="button" onClick={closeForm} aria-label={t('common.close')} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="dept-name">{t('common.name')}</label>
              <input
                id="dept-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('departments.placeholder_name')}
                className="mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="dept-desc">{t('common.description')}</label>
              <textarea
                id="dept-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder={t('common.optional')}
                className="mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400 resize-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={save} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                {editing ? t('common.save_changes') : t('departments.create')}
              </Button>
              <Button variant="outline" onClick={closeForm} disabled={saving}>{t('common.cancel')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-orange-100 dark:border-orange-500/20">
        <CardHeader className="rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <FolderTree className="w-5 h-5" /> {t('departments.all')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : departments.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">{t('departments.empty')}</div>
          ) : (
            <ul className="divide-y divide-border">
              {departments.map((d) => (
                <li key={d.id} className="py-3 flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">
                    <FolderTree className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground">{d.name}</div>
                    {d.description && <TruncatedText className="text-sm text-muted-foreground">{d.description}</TruncatedText>}
                  </div>
                  <button
                    type="button"
                    onClick={() => viewEmployees(d.name)}
                    title={t('departments.view_employees', 'View employees')}
                    className="shrink-0 text-sm text-muted-foreground hover:text-orange-600 hover:underline tabular-nums"
                  >
                    {employeeLabel(d.employee_count ?? 0)}
                  </button>
                  {d.is_active === false && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t('common.inactive')}</span>
                  )}
                  {canWrite && (
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => openEdit(d)} aria-label={t('common.edit', { name: d.name })}
                        className="p-1.5 rounded-[8px] text-muted-foreground hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-500/[0.12]">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => remove(d)} aria-label={t('common.delete', { name: d.name })}
                        className="p-1.5 rounded-[8px] text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/[0.12]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const DepartmentsPage: React.FC = () => (
  // Managing departments requires write; managers (department:read only) are
  // blocked from the page entirely, not just hidden from the nav.
  <RequirePermission perm="department:write">
    <DepartmentsInner />
  </RequirePermission>
);

export default DepartmentsPage;
